import { useEffect } from 'react';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener?: (type: 'release', cb: () => void) => void;
  removeEventListener?: (type: 'release', cb: () => void) => void;
}

interface WakeLockApi {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

interface NavigatorWithWakeLock {
  wakeLock?: WakeLockApi;
}

/**
 * Holds a screen wake lock while `enabled` is true. The browser releases the
 * lock automatically when the tab is hidden (visibilitychange → hidden), so
 * we re-acquire on the way back to visible. Safe no-op where unsupported
 * (iOS Safari < 16.4, Firefox).
 *
 * Use on pages where the cashier needs the screen to stay on (e.g. /orders,
 * /pickup) so the tablet doesn't sleep mid-shift.
 */
export function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined') return;
    const nav = navigator as Navigator & NavigatorWithWakeLock;
    if (!nav.wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        sentinel = await nav.wakeLock!.request('screen');
      } catch {
        /* user gesture required / other policy reason — silent */
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [enabled]);
}
